import React, { useState, useEffect } from 'react';
import { 
  Shield, 
  AlertTriangle, 
  Activity, 
  Lock, 
  Unlock, 
  Clock, 
  MapPin, 
  Smartphone, 
  Monitor,
  Filter,
  Eye,
  Download,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  Users,
  X
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

const SecurityMonitoring = () => {
  const [authAttempts, setAuthAttempts] = useState([]);
  const [securityStats, setSecurityStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [timeFilter, setTimeFilter] = useState('24h');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAttempt, setSelectedAttempt] = useState(null);
  const { userProfile } = useAuth();

  useEffect(() => {
    loadSecurityData();
  }, [timeFilter, statusFilter]);

  const loadSecurityData = async () => {
    setLoading(true);
    
  // Simulate API call - in a real implementation, fetch security events from
  // your backend (e.g. Firestore collections, Cloud Functions, or an audit log service)
    setTimeout(() => {
      const mockAttempts = [
        {
          id: '1',
          email: 'doctor@kmchospital.com',
          attempt_type: 'login',
          success: true,
          ip_address: '192.168.1.100',
          user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
          device_fingerprint: 'desktop_chrome_windows',
          created_at: new Date(Date.now() - 30 * 60 * 1000)?.toISOString(), // 30 minutes ago
          additional_data: {
            location: { city: 'Mumbai', country: 'India' },
            session_duration: '2h 15m'
          }
        },
        {
          id: '2',
          email: 'admin@kmchospital.com',
          attempt_type: 'login',
          success: false,
          failure_reason: 'Invalid password',
          ip_address: '103.25.45.123',
          user_agent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 17_0 like Mac OS X)',
          device_fingerprint: 'mobile_safari_ios',
          created_at: new Date(Date.now() - 45 * 60 * 1000)?.toISOString(), // 45 minutes ago
          additional_data: {
            location: { city: 'Delhi', country: 'India' },
            attempts_count: 3
          }
        },
        {
          id: '3',
          email: 'nurse@kmchospital.com',
          attempt_type: 'mfa_verify',
          success: true,
          ip_address: '192.168.1.105',
          user_agent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7)',
          device_fingerprint: 'desktop_safari_macos',
          created_at: new Date(Date.now() - 2 * 60 * 60 * 1000)?.toISOString(), // 2 hours ago
          additional_data: {
            location: { city: 'Mumbai', country: 'India' },
            mfa_method: 'authenticator'
          }
        },
        {
          id: '4',
          email: 'unknown@suspicious.com',
          attempt_type: 'login',
          success: false,
          failure_reason: 'Account not found',
          ip_address: '45.76.123.45',
          user_agent: 'curl/7.68.0',
          device_fingerprint: 'unknown_automated',
          created_at: new Date(Date.now() - 3 * 60 * 60 * 1000)?.toISOString(), // 3 hours ago
          additional_data: {
            location: { city: 'Unknown', country: 'Unknown' },
            suspicious: true,
            attempts_count: 15
          }
        },
        {
          id: '5',
          email: 'labtech@kmchospital.com',
          attempt_type: 'password_reset',
          success: true,
          ip_address: '192.168.1.112',
          user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
          device_fingerprint: 'desktop_chrome_windows',
          created_at: new Date(Date.now() - 4 * 60 * 60 * 1000)?.toISOString(), // 4 hours ago
          additional_data: {
            location: { city: 'Mumbai', country: 'India' },
            reset_method: 'email'
          }
        }
      ];

      const mockStats = {
        total_attempts: 156,
        successful_logins: 142,
        failed_attempts: 14,
        blocked_ips: 3,
        suspicious_activities: 5,
        unique_users: 45,
        peak_activity_hour: '09:00',
        trends: {
          successful_change: 5.2,
          failed_change: -12.8,
          unique_users_change: 8.1
        }
      };

      setAuthAttempts(mockAttempts);
      setSecurityStats(mockStats);
      setLoading(false);
    }, 1000);
  };

  const getDeviceIcon = (fingerprint) => {
    if (fingerprint?.includes('mobile')) return <Smartphone className="h-4 w-4" />;
    return <Monitor className="h-4 w-4" />;
  };

  const getAttemptStatusColor = (success, suspicious = false) => {
    if (suspicious) return 'bg-red-100 text-red-800';
    if (success) return 'bg-green-100 text-green-800';
    return 'bg-red-100 text-red-800';
  };

  const getAttemptStatusText = (success, suspicious = false) => {
    if (suspicious) return 'Suspicious';
    if (success) return 'Success';
    return 'Failed';
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    return `${hours}h ago`;
  };

  const exportSecurityReport = () => {
    // In real implementation, generate and download security report
    const report = {
      generated_at: new Date()?.toISOString(),
      time_period: timeFilter,
      stats: securityStats,
      attempts: authAttempts
    };
    
    const dataStr = JSON.stringify(report, null, 2);
    const dataBlob = new Blob([dataStr], {type: 'application/json'});
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `security-report-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const filteredAttempts = authAttempts?.filter(attempt => {
    if (statusFilter === 'success') return attempt?.success;
    if (statusFilter === 'failed') return !attempt?.success;
    if (statusFilter === 'suspicious') return attempt?.additional_data?.suspicious;
    return true;
  });

  const isAdmin = userProfile?.role === 'admin';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Security Monitoring</h2>
          <p className="text-gray-600">Monitor authentication attempts and security events</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={loadSecurityData}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          {isAdmin && (
            <button
              onClick={exportSecurityReport}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Download className="h-4 w-4" />
              <span>Export Report</span>
            </button>
          )}
        </div>
      </div>

      {/* Security Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Successful Logins</p>
              <p className="text-2xl font-bold text-green-600">{securityStats?.successful_logins || 0}</p>
              <div className="flex items-center mt-1">
                <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
                <span className="text-xs text-green-600">+{securityStats?.trends?.successful_change || 0}%</span>
              </div>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <Lock className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Failed Attempts</p>
              <p className="text-2xl font-bold text-red-600">{securityStats?.failed_attempts || 0}</p>
              <div className="flex items-center mt-1">
                <TrendingDown className="h-3 w-3 text-green-500 mr-1" />
                <span className="text-xs text-green-600">{securityStats?.trends?.failed_change || 0}%</span>
              </div>
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <Unlock className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Unique Users</p>
              <p className="text-2xl font-bold text-blue-600">{securityStats?.unique_users || 0}</p>
              <div className="flex items-center mt-1">
                <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
                <span className="text-xs text-green-600">+{securityStats?.trends?.unique_users_change || 0}%</span>
              </div>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Suspicious Activities</p>
              <p className="text-2xl font-bold text-orange-600">{securityStats?.suspicious_activities || 0}</p>
              <div className="flex items-center mt-1">
                <AlertTriangle className="h-3 w-3 text-orange-500 mr-1" />
                <span className="text-xs text-orange-600">Requires attention</span>
              </div>
            </div>
            <div className="p-3 bg-orange-100 rounded-full">
              <Shield className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">Filters:</span>
          </div>
          
          <select
            value={timeFilter}
            onChange={(e) => setTimeFilter(e?.target?.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24 Hours</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e?.target?.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Attempts</option>
            <option value="success">Successful Only</option>
            <option value="failed">Failed Only</option>
            <option value="suspicious">Suspicious Only</option>
          </select>
        </div>
      </div>

      {/* Authentication Attempts */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Authentication Attempts</h3>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Loading security data...</p>
          </div>
        ) : filteredAttempts?.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No authentication attempts found for the selected filters
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredAttempts?.map((attempt) => (
              <div key={attempt?.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="flex-shrink-0">
                      <div className={`p-2 rounded-full ${
                        attempt?.additional_data?.suspicious ? 'bg-red-100 text-red-600' : attempt?.success ?'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
                      }`}>
                        {getDeviceIcon(attempt?.device_fingerprint)}
                      </div>
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <h4 className="text-sm font-medium text-gray-900">{attempt?.email}</h4>
                        
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          getAttemptStatusColor(attempt?.success, attempt?.additional_data?.suspicious)
                        }`}>
                          {getAttemptStatusText(attempt?.success, attempt?.additional_data?.suspicious)}
                        </span>
                        
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                          {attempt?.attempt_type?.replace('_', ' ')}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <Activity className="h-4 w-4" />
                            <span>IP: {attempt?.ip_address}</span>
                          </div>
                          {attempt?.additional_data?.location && (
                            <div className="flex items-center space-x-2">
                              <MapPin className="h-4 w-4" />
                              <span>{attempt?.additional_data?.location?.city}, {attempt?.additional_data?.location?.country}</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="space-y-1">
                          <div className="flex items-center space-x-2">
                            <Clock className="h-4 w-4" />
                            <span>{formatTime(attempt?.created_at)}</span>
                          </div>
                          {attempt?.failure_reason && (
                            <div className="text-red-600">
                              Reason: {attempt?.failure_reason}
                            </div>
                          )}
                        </div>
                        
                        <div className="space-y-1">
                          {attempt?.additional_data?.attempts_count && (
                            <div className="text-orange-600">
                              Attempts: {attempt?.additional_data?.attempts_count}
                            </div>
                          )}
                          {attempt?.additional_data?.mfa_method && (
                            <div className="text-green-600">
                              MFA: {attempt?.additional_data?.mfa_method}
                            </div>
                          )}
                          {attempt?.additional_data?.session_duration && (
                            <div className="text-blue-600">
                              Duration: {attempt?.additional_data?.session_duration}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="mt-2 text-xs text-gray-500">
                        User Agent: {attempt?.user_agent?.substring(0, 80)}...
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => setSelectedAttempt(attempt)}
                      className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      title="View details"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Security Recommendations */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-6">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="p-3 bg-blue-100 rounded-full">
              <Shield className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Security Recommendations</h3>
            <div className="space-y-2 text-sm text-gray-700">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span>Enable multi-factor authentication for all admin accounts</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span>Monitor failed login attempts and implement account lockout policies</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span>Regular security audits and password strength enforcement</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                <span>Implement IP whitelisting for administrative access</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Attempt Details Modal */}
      {selectedAttempt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Authentication Attempt Details</h2>
                <button
                  onClick={() => setSelectedAttempt(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <p className="text-sm text-gray-900">{selectedAttempt?.email}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    getAttemptStatusColor(selectedAttempt?.success, selectedAttempt?.additional_data?.suspicious)
                  }`}>
                    {getAttemptStatusText(selectedAttempt?.success, selectedAttempt?.additional_data?.suspicious)}
                  </span>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Attempt Type</label>
                  <p className="text-sm text-gray-900 capitalize">{selectedAttempt?.attempt_type?.replace('_', ' ')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Timestamp</label>
                  <p className="text-sm text-gray-900">{(() => { const d = new Date(selectedAttempt?.created_at); return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; })()}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">IP Address</label>
                  <p className="text-sm text-gray-900">{selectedAttempt?.ip_address}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <p className="text-sm text-gray-900">
                    {selectedAttempt?.additional_data?.location?.city}, {selectedAttempt?.additional_data?.location?.country}
                  </p>
                </div>

                {selectedAttempt?.failure_reason && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Failure Reason</label>
                    <p className="text-sm text-red-600">{selectedAttempt?.failure_reason}</p>
                  </div>
                )}

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">User Agent</label>
                  <p className="text-sm text-gray-900 break-all">{selectedAttempt?.user_agent}</p>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Device Fingerprint</label>
                  <p className="text-sm text-gray-900">{selectedAttempt?.device_fingerprint}</p>
                </div>

                {selectedAttempt?.additional_data && Object.keys(selectedAttempt?.additional_data)?.length > 0 && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Additional Data</label>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <pre className="text-xs text-gray-700 overflow-x-auto">
                        {JSON.stringify(selectedAttempt?.additional_data, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecurityMonitoring;